import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useRef } from "react";

import { GqlProjectGetAutocompleteOptionsParams } from "~/__generated__";

import { ProjectSelect_GetAutocompleteOptionsDocument, ProjectSelect_GetAutocompleteOptionsQuery } from "../queries.generated";

type ProjectOption = ProjectSelect_GetAutocompleteOptionsQuery["projectGetAutocompleteOptions"]["data"][number];

export default function useProjectSelect_GetAutocompleteOptions(params: GqlProjectGetAutocompleteOptionsParams) {
    const knownProjects = useRef<ProjectOption[]>([]);

    const selectedIds = params.selectedIds ?? [];
    const clientIds = params.clientIds ?? [];

    // Drop `selectedIds` whose client is not (or no longer) part of the client filter: the backend
    // rejects ids outside the client scope. Ids we have not seen yet are kept.
    const scrubbedSelectedIds =
        clientIds.length > 0
            ? selectedIds.filter(projectId => {
                  const project = knownProjects.current.find(option => option.value === projectId);
                  return project === undefined || clientIds.includes(project.clientId);
              })
            : selectedIds;

    // The scrub only ever removes ids, so a length difference means something was dropped.
    const selectedIdsIsScrubbed = scrubbedSelectedIds.length !== selectedIds.length;

    const { data, previousData, loading } = useQuery(ProjectSelect_GetAutocompleteOptionsDocument, {
        variables: { params: { ...params, selectedIds: scrubbedSelectedIds } },
    });

    // Remember the client of every project the backend returns so future requests can be scoped.
    useEffect(() => {
        const payload = data?.projectGetAutocompleteOptions;
        if (!payload) return;

        knownProjects.current = [...payload.data, ...payload.selectedData];
    }, [data]);

    const options = useMemo(() => data?.projectGetAutocompleteOptions.data ?? [], [data]);

    const selectedOptions = useMemo(
        () => (data ?? previousData)?.projectGetAutocompleteOptions.selectedData ?? [],
        [data, previousData],
    );

    return { options, selectedOptions, scrubbedSelectedIds, selectedIdsIsScrubbed, loading };
}
